import {
  Component,
  ElementRef,
  inject,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { Subscription } from 'rxjs';

import { AllFiltersComponent } from 'app/modules/browse/components/all-filters/all-filters.component';
import { ModeNamesEnum } from 'app/modules/browse/models/browse-mode';
import { BrowseFilter } from 'app/modules/browse/models/filter';
import { MapService } from 'app/modules/browse/services/map.service';
import { FilterStore } from 'app/modules/browse/store/filter/filter.store';
import { MapStore } from 'app/modules/browse/store/map/map.store';
import { ModalService } from 'app/services/modal.service';
import { ResponsiveService } from 'app/services/responsive.service';
import { LARGE_SCREEN_BREAKPOINT } from 'src/environments/environment.local';
import * as filterHelpers from '../../../../helpers/filter.helpers';
import { TranslateModule } from '@ngx-translate/core';
import { LoaderComponent } from '../../../../../../shared/components/loader/loader.component';
import { NgClass, NgStyle, NgTemplateOutlet } from '@angular/common';
import {
  VideoSection,
  ScenicCardComponent,
  HorizontalScrollerComponent,
} from '@kinomap-dev/ng-ui-lib';
import { Router } from '@angular/router';

@Component({
  selector: 'app-map-list',
  templateUrl: './map-list.component.html',
  styleUrls: ['./map-list.component.scss'],
  standalone: true,
  imports: [
    NgClass,
    NgStyle,
    NgTemplateOutlet,
    LoaderComponent,
    TranslateModule,
    ScenicCardComponent,
    HorizontalScrollerComponent,
  ],
})
export class MapListComponent implements OnInit, OnDestroy {
  @ViewChild('cardsContainer') scrollBox: ElementRef;

  @Input() path!: 'playlist' | 'myVideos' | 'videos';

  private mapService: MapService = inject(MapService);
  private mapStore: MapStore = inject(MapStore);
  private modalService: ModalService = inject(ModalService);
  private filterStore: FilterStore = inject(FilterStore);
  private responsiveService: ResponsiveService = inject(ResponsiveService);
  private router: Router = inject(Router);

  private readonly subscriptions: Subscription[] = [];
  private readonly gapBetweenVideos: number = 10;
  private readonly paddingTop: number = 100;
  private isVideoSelectedFromTheList: boolean = false;
  private modalRenderedSubscription: Subscription | undefined;

  isLoading: boolean = false;
  videos: VideoSection[] | null = null;
  selectedVideo: VideoSection | null = null;
  filters: BrowseFilter[] = [];
  isSelectingFilters: boolean = false;
  isLargeScreen: boolean = true;

  get numberOfActiveFilters(): number {
    return this.filters.filter((f: BrowseFilter) =>
      filterHelpers.isFilterActive(f)
    ).length;
  }

  get isPotentialContentAvailable(): boolean {
    return this.isLoading || (!!this.videos && !!this.videos.length);
  }

  navigateToVideo(videoId: number): void {
    this.mapService.setSelectedVideo(null);
    this.router.navigate(['browse', 'watch', videoId]);
  }

  ngOnInit(): void {
    this.subscriptions.push(
      this.mapStore.videos$.subscribe((videos: VideoSection[]) => {
        this.videos = videos;
      }),
      this.mapStore.isLoading$.subscribe(
        (isLoading: boolean) => (this.isLoading = isLoading)
      ),
      this.mapService.selectedVideo$.subscribe((video: VideoSection | null) => {
        this.selectedVideo = video;
        if (!video) {
          return;
        }

        if (this.isVideoSelectedFromTheList) {
          this.isVideoSelectedFromTheList = false;
        } else {
          this.scrollToSelectedVideo();
        }
      }),
      this.filterStore.mapFilters$.subscribe((filters: BrowseFilter[]) => {
        this.filters = filters.filter(
          (f: BrowseFilter) => f.name !== 'sortDirection'
        );
      }),
      this.responsiveService.width$.subscribe((width: number) => {
        this.isLargeScreen = width > LARGE_SCREEN_BREAKPOINT;
      }),
      this.modalService.onModalClose.subscribe(() => {
        this.isSelectingFilters = false;
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub: Subscription) => sub.unsubscribe());
    this.modalRenderedSubscription?.unsubscribe();
  }

  onSelectedOrUnselectedVideo(videoId?: number): void {
    if (!videoId) {
      this.mapService.setSelectedVideo(null);
      return;
    }
    this.isVideoSelectedFromTheList = true;
    const videoToSelect: VideoSection = this.videos.find(
      (v: VideoSection) => v.id === videoId
    );
    this.mapService.setSelectedVideo(videoToSelect);
  }

  onDisplayFilters(): void {
    this.isSelectingFilters = true;
    this.modalService.display();
    this.modalRenderedSubscription?.unsubscribe();

    this.modalService.setDialogComponent(AllFiltersComponent);

    if (this.modalService.dialogComponent instanceof AllFiltersComponent) {
      const component: AllFiltersComponent = this.modalService
        .dialogComponent as AllFiltersComponent;
      component.reset({
        modeName: ModeNamesEnum.Videos,
        isSearch: true,
        isMap: true,
      });
    } else {
      this.modalRenderedSubscription =
        this.modalService.onModalRendered.subscribe(
          (component: AllFiltersComponent) => {
            component.reset({
              modeName: ModeNamesEnum.Videos,
              isSearch: true,
              isMap: true,
            });
          }
        );
    }
  }

  scrollToSelectedVideo(): void {
    if (this.isLargeScreen) {
      const videoEl: HTMLElement = document.getElementById(
        `mapVideo${this.selectedVideo.id}`
      );
      const videoElY: number = videoEl.getBoundingClientRect().y;
      const scrollBoxY: number =
        this.scrollBox.nativeElement.getBoundingClientRect().y;
      this.scrollBox.nativeElement.scrollBy({
        top: videoElY - this.gapBetweenVideos - this.paddingTop - scrollBoxY,
        behavior: 'smooth',
      });
      return;
    }
  }
}
